import { Link, useLocation } from 'react-router-dom';
import AppLayout from '../../layouts/AppLayout';
import { formatRequestId } from '../../utils/RequestIdHelper';
import { formatStatusForDisplay } from '../../utils/StatusDisplayHelper';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const normalizeText = (value) => {
    if (typeof value === 'string') {
        return value.trim();
    }

    if (value == null) {
        return '';
    }

    return String(value).trim();
};

const sanitizeDisplayList = (values) => {
    if (!Array.isArray(values)) {
        return [];
    }

    const deduped = new Set();
    values.forEach((value) => {
        const normalized = normalizeText(value);
        if (!normalized || UUID_PATTERN.test(normalized)) {
            return;
        }
        deduped.add(normalized);
    });

    return Array.from(deduped);
};

const RequestResultsPage = ({ authDetails, onSignOut }) => {
    const location = useLocation();
    const responseData = location.state?.response ?? null;
    const requestPayload = location.state?.requestPayload ?? null;
    const errorDetails = location.state?.error ?? null;
    const hasError = Boolean(errorDetails);
    const responseItems = Array.isArray(responseData)
        ? responseData
        : responseData
            ? [responseData]
            : [];
    const primaryResponseItem = responseItems[0] ?? null;

    const submittedGroupNames = sanitizeDisplayList(requestPayload?.groupNames);
    const submittedRoleNames = sanitizeDisplayList(requestPayload?.roleNames ?? requestPayload?.roles ?? []);
    const submittedGroupFallback = sanitizeDisplayList([
        ...(Array.isArray(requestPayload?.groups) ? requestPayload.groups : []),
        requestPayload?.groupUuid,
        ...responseItems.map((item) => item?.groupUuid),
    ]);
    const submittedGroups = submittedGroupNames.length ? submittedGroupNames : submittedGroupFallback;
    const submittedRoles = submittedRoleNames;
    const hasRequestedRoles = submittedRoles.length > 0;
    const hasRequestedEntitlements = submittedGroups.length > 0 || submittedRoles.length > 0;
    const requestedEntitlementLabel = hasRequestedRoles ? 'Requested roles' : 'Requested groups';
    const requestedEntitlementValues = hasRequestedRoles ? submittedRoles : submittedGroups;
    const submittedState = formatStatusForDisplay(
        requestPayload?.state ?? primaryResponseItem?.state,
        'Submitted'
    );
    const supervisorName = sanitizeDisplayList([
        requestPayload?.securityManagerName,
        requestPayload?.supervisorName,
        primaryResponseItem?.securityManagerName,
        primaryResponseItem?.supervisorName,
        requestPayload?.securityManagerUuid,
        primaryResponseItem?.securityManagerUuid,
    ])[0];
    const requestIds = responseItems
        .map((item) => item?.id ?? item?.requestId ?? item?.uuid ?? item?.accessRequestId ?? null)
        .filter(Boolean);
    const displayRequestIds = requestIds.map((id) => formatRequestId(id));
    const hasSingleRequestId = displayRequestIds.length === 1;
    const hasMultipleRequestIds = displayRequestIds.length > 1;

    return (
        <AppLayout sidebar authDetails={authDetails} onSignOut={onSignOut}>
            <section className="usa-section">
                <div className="grid-container">
                    <div className="usa-prose">
                        <h1>{hasError ? 'Request failed' : 'Request submitted'}</h1>
                        <p>
                            {hasError
                                ? 'We were unable to submit your access request. Review the error details below.'
                                : 'Your access request has been submitted. Review the summary below.'}
                        </p>
                        <div
                            className={`usa-alert ${hasError ? 'usa-alert--error' : 'usa-alert--success'}`}
                            role="status"
                        >
                            <div className="usa-alert__body">
                                <p className="usa-alert__text">
                                    {hasError
                                        ? 'Submission failed.'
                                        : 'Submission complete.'}
                                </p>
                            </div>
                        </div>
                        {hasError ? (
                            <>
                                <h2>Request payload</h2>
                                <pre className="usa-code">
                                    {JSON.stringify(requestPayload ?? { message: 'No payload available.' }, null, 2)}
                                </pre>
                                <h2>Error response</h2>
                                <pre className="usa-code">
                                    {JSON.stringify(errorDetails, null, 2)}
                                </pre>
                            </>
                        ) : (
                            <>
                                <h2>Submission summary</h2>
                                <div className="usa-summary-box" role="region" aria-labelledby="submission-summary">
                                    <div className="usa-summary-box__body">
                                        <h3 className="usa-summary-box__heading" id="submission-summary">
                                            Request details
                                        </h3>
                                        <div className="usa-summary-box__text">
                                            {hasSingleRequestId ? (
                                                <p>
                                                    <span className="approval-key">Request ID</span>
                                                    {displayRequestIds[0]}
                                                </p>
                                            ) : hasMultipleRequestIds ? (
                                                <p>
                                                    <span className="approval-key">Request IDs</span>
                                                    {displayRequestIds.join(', ')}
                                                </p>
                                            ) : null}
                                            <p>
                                                <span className="approval-key">Status</span>
                                                {submittedState}
                                            </p>
                                            {supervisorName ? (
                                                <p>
                                                    <span className="approval-key">Supervisor</span>
                                                    {supervisorName}
                                                </p>
                                            ) : null}
                                            <p>
                                                <span className="approval-key">{requestedEntitlementLabel}</span>
                                                {requestedEntitlementValues.length
                                                    ? requestedEntitlementValues.join(', ')
                                                    : hasRequestedEntitlements
                                                        ? 'None selected'
                                                        : 'Unavailable'}
                                            </p>
                                            {!hasRequestedEntitlements ? (
                                                <p className="text-secondary">
                                                    No groups or roles were captured for this request. Please contact support.
                                                </p>
                                            ) : null}
                                            <p>
                                                <span className="approval-key">Job responsibilities</span>
                                                {requestPayload?.jobResponsibilities || 'Not provided'}
                                            </p>
                                            <p>
                                                <span className="approval-key">Justification</span>
                                                {requestPayload?.justification || 'Not provided'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                        <Link to="/requests" className="usa-button usa-button--outline">
                            Back to requests
                        </Link>
                    </div>
                </div>
            </section>
        </AppLayout>
    );
};

export default RequestResultsPage;
